import { Temporal as TC39Temporal } from "temporal-polyfill";
import {
  formatCalendarDay,
  formatInstantWeekdayClock,
  formatTemporal,
  formatTemporalWithTime,
  temporalDayIn,
  todayInZone,
  viewerTimeZone,
} from "@/utils/temporal";
import type { Scalar, TemporalDisplay } from "./types";

/** Preserve kind defaults unless the component explicitly selects a precision. */
function fractionDigits(
  scalar: Extract<Scalar, { kind: "number" | "percent" | "currency" }>
): Intl.NumberFormatOptions {
  if (scalar.max_precision !== null && scalar.max_precision !== undefined) {
    return {
      minimumFractionDigits: 0,
      maximumFractionDigits: scalar.max_precision,
    };
  }
  if (scalar.precision !== null) {
    return {
      minimumFractionDigits: scalar.precision,
      maximumFractionDigits: scalar.precision,
    };
  }
  switch (scalar.kind) {
    case "number":
      return { maximumFractionDigits: 6 };
    case "percent":
      return { minimumFractionDigits: 0, maximumFractionDigits: 2 };
    case "currency":
      return {};
  }
}

/** A day in a list row: the year only when it is not the viewer's current one. */
function compactDay(
  day: string,
  display: TemporalDisplay,
  zone: string,
  now: Date
): string {
  const thisYear = day.slice(0, 4) === todayInZone(zone, now).slice(0, 4);
  if (display === "day") {
    return formatCalendarDay(day, thisYear ? "monthDay" : "medium");
  }
  return formatCalendarDay(day, thisYear ? "weekdayMonthDay" : "weekdayMedium");
}

export function formatScalar(
  scalar: Scalar,
  zone = viewerTimeZone(),
  now = new Date()
): string {
  if (scalar.value === null || scalar.value === "") return "--";
  switch (scalar.kind) {
    case "text":
      return scalar.value;
    case "boolean":
      return scalar.value ? "Yes" : "No";
    case "date":
      return scalar.display
        ? compactDay(scalar.value.day, scalar.display, zone, now)
        : formatTemporal(scalar.value, "medium");
    case "instant":
      switch (scalar.display) {
        case "day":
          return compactDay(
            temporalDayIn(scalar.value, zone),
            "day",
            zone,
            now
          );
        case "agenda":
          return formatInstantWeekdayClock(scalar.value.at, zone, now);
        default:
          return formatTemporalWithTime({ ...scalar.value, zone }, "medium");
      }
    case "number": {
      const formatted = new Intl.NumberFormat(
        "en-US",
        fractionDigits(scalar)
      ).format(scalar.value);
      return scalar.unit ? `${formatted} ${scalar.unit}` : formatted;
    }
    case "percent":
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        ...fractionDigits(scalar),
      }).format(scalar.scale === "percent" ? scalar.value / 100 : scalar.value);
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: scalar.currency,
        ...fractionDigits(scalar),
      }).format(scalar.value);
    default: {
      const unhandled: never = scalar;
      throw new Error(
        `Unsupported component scalar: ${JSON.stringify(unhandled)}`
      );
    }
  }
}

function sortableValue(scalar: Scalar): string | number | bigint | null {
  if (scalar.value === null || scalar.value === "") return null;
  switch (scalar.kind) {
    case "text":
      return scalar.value;
    case "boolean":
      return Number(scalar.value);
    case "date":
      return scalar.value.day;
    case "instant":
      return TC39Temporal.Instant.from(scalar.value.at).epochNanoseconds;
    case "number":
    case "currency":
      return scalar.value;
    case "percent":
      return scalar.scale === "percent" ? scalar.value / 100 : scalar.value;
    default: {
      const unhandled: never = scalar;
      throw new Error(
        `Unsupported component scalar: ${JSON.stringify(unhandled)}`
      );
    }
  }
}

/** Text without the commas that group digits ("3,329" → "3329"); other commas stay. */
function withoutDigitGroups(text: string): string {
  return text.replace(/(\d),(?=\d{3}(?!\d))/g, "$1");
}

/** Missing values sort last in ascending order; callers retain that position when reversing. */
export function compareScalars(left: Scalar, right: Scalar): number {
  const a = sortableValue(left),
    b = sortableValue(right);
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  // A digit-group comma ends a digit run for numeric collation, so "3,329" would sort
  // below "999" (#15136). Compare without them; display keeps them. When only one side is
  // text (a text column mixing a typed value with prose), both collate as text so the order
  // is deterministic.
  if (typeof a === "string" || typeof b === "string")
    return withoutDigitGroups(String(a)).localeCompare(
      withoutDigitGroups(String(b)),
      "en-US",
      { numeric: true, sensitivity: "base" }
    );
  return a < b ? -1 : a > b ? 1 : 0;
}
